import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  ComposedChart
} from 'recharts';
import {
  Download,
  ArrowBack,
  TrendingUp,
  AccountBalance,
  AttachMoney,
  Refresh,
  ShowChart,
  Equalizer,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  Info,
  CalendarToday,
  PointOfSale,
  ShoppingCart
} from '@mui/icons-material';
import reportService from '../../../services/reportService';
import excelExport from '../../../utils/excelExport';
import DateRangeFilter from './DateRangeFilter';
import './FinancialReports.css';

// Currency formatter for LKR
const formatLKR = (amount) => {
  if (amount === null || amount === undefined) return 'LKR 0';
  return `LKR ${parseFloat(amount).toLocaleString('en-LK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

const formatNumber = (number) => {
  if (number === null || number === undefined) return '0';
  return number.toLocaleString('en-LK');
};

const FinancialReports = ({ onPageChange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportType, setReportType] = useState('net_profit');
  const [reportData, setReportData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filters, setFilters] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });

  const reportTypes = [
    { 
      value: 'net_profit', 
      label: 'Profit & Loss', 
      icon: <AttachMoney />,
      description: 'Comprehensive profit and loss analysis'
    },
    { 
      value: 'revenue_trends', 
      label: 'Revenue Trends', 
      icon: <TrendingUp />,
      description: 'Revenue performance over time'
    },
    { 
      value: 'profit_margins', 
      label: 'Margin Analysis', 
      icon: <AccountBalance />,
      description: 'Product and category profit margins'
    }
  ];

  // Color schemes
  const chartColors = {
    revenue: '#10B981',
    cost: '#EF4444',
    grossProfit: '#3B82F6',
    netProfit: '#F59E0B',
    pos: '#6366F1',
    ecommerce: '#8B5CF6',
    total: '#EC4899',
    positive: '#10B981',
    negative: '#EF4444',
    neutral: '#6B7280'
  };

  useEffect(() => {
    loadReportData();
  }, [reportType, filters]);

  const loadReportData = async () => {
    setLoading(true);
    setError('');
    try {
      let response;
      switch (reportType) {
        case 'net_profit':
          response = await reportService.getNetProfit(filters);
          break;
        case 'revenue_trends':
          response = await reportService.getDailySalesAmount(filters);
          break;
        case 'profit_margins':
          response = await reportService.getProductPerformance(filters);
          break;
        default:
          response = await reportService.getNetProfit(filters);
      }
      setReportData(response.data);
      setLastUpdated(new Date());
    } catch (err) {
      setError('Failed to load financial report data: ' + (err.message || 'Unknown error'));
      console.error('Financial report loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!reportData) return;
    
    try {
      switch (reportType) {
        case 'net_profit':
          excelExport.exportNetProfit(reportData);
          break;
        case 'revenue_trends':
          excelExport.exportDailySales(reportData);
          break;
        case 'profit_margins':
          excelExport.exportProductPerformance(reportData);
          break;
        default:
          excelExport.exportToExcel(reportData, `${reportType}_financial_report`);
      }
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export data: ' + err.message);
    }
  };

  const renderMetricCard = (title, value, subtitle = '', format = 'currency', color = 'primary', trend = null) => {
    const formattedValue = format === 'currency' ? formatLKR(value) : formatNumber(value);

    return (
      <Card className="fin-metric-card">
        <CardContent className="fin-metric-content">
          <div className="fin-metric-header">
            <Typography className="fin-metric-title">
              {title}
            </Typography>
            {subtitle && (
              <Tooltip title={subtitle}>
                <Info className="fin-metric-info" />
              </Tooltip>
            )}
          </div>
          <Typography className={`fin-metric-value fin-metric-${color}`}>
            {formattedValue}
          </Typography>
          {trend && (
            <div className={`fin-metric-trend fin-trend-${trend.direction}`}>
              {trend.direction === 'up' ? (
                <TrendingUp className="fin-trend-icon" />
              ) : trend.direction === 'down' ? (
                <TrendingUp className="fin-trend-icon fin-trend-down" />
              ) : null}
              <Typography className="fin-trend-text">
                {trend.value}% {trend.direction === 'up' ? 'increase' : 'decrease'}
              </Typography>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderNetProfitAnalysis = () => {
    if (!reportData?.profit_breakdown) return null;

    const profitData = [
      {
        name: 'Revenue',
        value: reportData.profit_breakdown.total_revenue,
        color: chartColors.revenue
      },
      {
        name: 'Cost',
        value: reportData.profit_breakdown.total_cost,
        color: chartColors.cost
      },
      {
        name: 'Gross Profit',
        value: reportData.profit_breakdown.gross_profit,
        color: chartColors.grossProfit
      },
      {
        name: 'Net Profit',
        value: reportData.profit_breakdown.net_profit,
        color: chartColors.netProfit
      }
    ];

    const channelComparison = [
      {
        name: 'POS',
        revenue: reportData.pos_breakdown?.revenue || 0,
        profit: reportData.pos_breakdown?.gross_profit || 0,
        margin: reportData.pos_breakdown?.gross_profit_margin || 0
      },
      {
        name: 'Ecommerce',
        revenue: reportData.ecommerce_breakdown?.revenue || 0,
        profit: reportData.ecommerce_breakdown?.gross_profit || 0,
        margin: reportData.ecommerce_breakdown?.gross_profit_margin || 0
      }
    ];

    return (
      <div className="fin-report-container">
        {/* Summary Metrics */}
        <div className="fin-metrics-grid">
          {renderMetricCard(
            'Total Revenue',
            reportData.profit_breakdown.total_revenue,
            'Total revenue from all channels',
            'currency',
            'success'
          )}
          {renderMetricCard(
            'Total Cost',
            reportData.profit_breakdown.total_cost,
            'Total cost of goods sold',
            'currency',
            'error'
          )}
          {renderMetricCard(
            'Gross Profit',
            reportData.profit_breakdown.gross_profit,
            `Gross profit margin: ${reportData.profit_breakdown.gross_profit_margin?.toFixed(2)}%`,
            'currency',
            'info'
          )}
          {renderMetricCard(
            'Net Profit',
            reportData.profit_breakdown.net_profit,
            `Net profit margin: ${reportData.profit_breakdown.net_profit_margin?.toFixed(2)}%`,
            'currency',
            'warning'
          )}
        </div>

        {/* Main Profit & Loss Chart */}
        <Card className="fin-chart-card">
          <CardContent className="fin-chart-content">
            <div className="fin-chart-header">
              <BarChartIcon className="fin-chart-icon" />
              <Typography className="fin-chart-title">Profit & Loss Breakdown</Typography>
            </div>
            <div className="fin-chart-container">
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={profitData} className="fin-composed-chart">
                  <CartesianGrid strokeDasharray="3 3" className="fin-chart-grid" />
                  <XAxis dataKey="name" className="fin-chart-axis" />
                  <YAxis 
                    tickFormatter={(value) => `LKR ${value / 1000}k`}
                    className="fin-chart-axis"
                  />
                  <RechartsTooltip 
                    formatter={(value) => [formatLKR(value), 'Amount']}
                    className="fin-chart-tooltip"
                  />
                  <Legend className="fin-chart-legend" />
                  <Bar dataKey="value" name="Amount" radius={[6, 6, 0, 0]}>
                    {profitData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#000"
                    strokeWidth={2}
                    dot={{ fill: '#000', strokeWidth: 2, r: 4 }}
                    strokeDasharray="5 5"
                    name="Trend"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Channel Performance */}
        <div className="fin-charts-row">
          <Card className="fin-chart-card fin-chart-half">
            <CardContent className="fin-chart-content">
              <div className="fin-chart-header">
                <Equalizer className="fin-chart-icon" />
                <Typography className="fin-chart-title">Channel Performance</Typography>
              </div>
              <div className="fin-chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={channelComparison} layout="vertical" className="fin-bar-chart">
                    <CartesianGrid strokeDasharray="3 3" className="fin-chart-grid" horizontal={false} />
                    <XAxis 
                      type="number"
                      tickFormatter={(value) => `LKR ${value / 1000}k`}
                      className="fin-chart-axis"
                    />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      width={80}
                      className="fin-chart-axis"
                    />
                    <RechartsTooltip 
                      formatter={(value, name) => {
                        if (name === 'Margin') return [`${value}%`, name];
                        return [formatLKR(value), name];
                      }}
                      className="fin-chart-tooltip"
                    />
                    <Legend className="fin-chart-legend" />
                    <Bar dataKey="revenue" fill={chartColors.pos} name="Revenue" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="profit" fill={chartColors.grossProfit} name="Gross Profit" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <div className="fin-side-cards">
            <Card className="fin-mini-card">
              <CardContent className="fin-mini-content">
                <div className="fin-mini-header">
                  <PointOfSale className="fin-mini-icon" />
                  <Typography className="fin-mini-title">POS Performance</Typography>
                </div>
                <div className="fin-mini-metrics">
                  <div className="fin-mini-metric">
                    <Typography className="fin-mini-label">Revenue</Typography>
                    <Typography className="fin-mini-value fin-revenue">
                      {formatLKR(reportData.pos_breakdown?.revenue)}
                    </Typography>
                  </div>
                  <div className="fin-mini-metric">
                    <Typography className="fin-mini-label">Cost</Typography>
                    <Typography className="fin-mini-value fin-cost">
                      {formatLKR(reportData.pos_breakdown?.cost)}
                    </Typography>
                  </div>
                  <div className="fin-mini-metric">
                    <Typography className="fin-mini-label">Gross Profit</Typography>
                    <Typography className="fin-mini-value fin-profit">
                      {formatLKR(reportData.pos_breakdown?.gross_profit)}
                    </Typography>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="fin-mini-card">
              <CardContent className="fin-mini-content">
                <div className="fin-mini-header">
                  <ShoppingCart className="fin-mini-icon" />
                  <Typography className="fin-mini-title">Ecommerce Performance</Typography>
                </div>
                <div className="fin-mini-metrics">
                  <div className="fin-mini-metric">
                    <Typography className="fin-mini-label">Revenue</Typography>
                    <Typography className="fin-mini-value fin-revenue">
                      {formatLKR(reportData.ecommerce_breakdown?.revenue)}
                    </Typography>
                  </div>
                  <div className="fin-mini-metric">
                    <Typography className="fin-mini-label">Cost</Typography>
                    <Typography className="fin-mini-value fin-cost">
                      {formatLKR(reportData.ecommerce_breakdown?.cost)}
                    </Typography>
                  </div>
                  <div className="fin-mini-metric">
                    <Typography className="fin-mini-label">Gross Profit</Typography>
                    <Typography className="fin-mini-value fin-profit">
                      {formatLKR(reportData.ecommerce_breakdown?.gross_profit)}
                    </Typography>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  };

  const renderRevenueTrends = () => {
    if (!reportData?.daily_sales) return null;

    return (
      <div className="fin-report-container">
        {/* Summary Metrics */}
        <div className="fin-metrics-grid">
          {renderMetricCard(
            'Total Revenue',
            reportData.summary?.total_sales,
            'Total revenue across all channels',
            'currency',
            'success'
          )}
          {renderMetricCard(
            'POS Revenue',
            reportData.summary?.total_pos_sales,
            'Revenue from POS channel',
            'currency',
            'info'
          )}
          {renderMetricCard(
            'Ecommerce Revenue',
            reportData.summary?.total_ecommerce_sales,
            'Revenue from Ecommerce channel',
            'currency',
            'warning'
          )}
          {renderMetricCard(
            'Avg. Daily Revenue',
            reportData.summary?.average_daily_sales,
            'Average daily revenue',
            'currency',
            'primary'
          )}
        </div>

        {/* Revenue Trends Chart */}
        <Card className="fin-chart-card">
          <CardContent className="fin-chart-content">
            <div className="fin-chart-header">
              <ShowChart className="fin-chart-icon" />
              <Typography className="fin-chart-title">Revenue Trends Over Time</Typography>
            </div>
            <div className="fin-chart-container">
              <ResponsiveContainer width="100%" height={400}>
                <AreaChart data={reportData.daily_sales} className="fin-area-chart">
                  <defs>
                    <linearGradient id="totalRevenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={chartColors.total} stopOpacity={0.4}/>
                      <stop offset="95%" stopColor={chartColors.total} stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id="posRevenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={chartColors.pos} stopOpacity={0.4}/>
                      <stop offset="95%" stopColor={chartColors.pos} stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id="ecommerceRevenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={chartColors.ecommerce} stopOpacity={0.4}/>
                      <stop offset="95%" stopColor={chartColors.ecommerce} stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="fin-chart-grid" />
                  <XAxis dataKey="date" className="fin-chart-axis" />
                  <YAxis 
                    tickFormatter={(value) => `LKR ${value / 1000}k`}
                    className="fin-chart-axis"
                  />
                  <RechartsTooltip 
                    formatter={(value) => [formatLKR(value), 'Revenue']}
                    className="fin-chart-tooltip"
                  />
                  <Legend className="fin-chart-legend" />
                  <Area 
                    type="monotone" 
                    dataKey="total_sales" 
                    stroke={chartColors.total} 
                    fill="url(#totalRevenueGradient)" 
                    name="Total Revenue" 
                    strokeWidth={3}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="pos_sales" 
                    stroke={chartColors.pos} 
                    fill="url(#posRevenueGradient)" 
                    name="POS Revenue" 
                    strokeWidth={2}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="ecommerce_sales" 
                    stroke={chartColors.ecommerce} 
                    fill="url(#ecommerceRevenueGradient)" 
                    name="Ecommerce Revenue" 
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderProfitMargins = () => {
    if (!reportData?.top_products_pos && !reportData?.top_products_ecommerce) return null;

    const topProducts = [
      ...(reportData.top_products_pos || []).slice(0, 10),
      ...(reportData.top_products_ecommerce || []).slice(0, 10)
    ].sort((a, b) => (b.profit_margin || 0) - (a.profit_margin || 0)).slice(0, 15);

    return (
      <div className="fin-report-container">
        {/* Summary Metrics */}
        <div className="fin-metrics-grid">
          {renderMetricCard(
            'Avg. Profit Margin',
            reportData.summary?.average_profit_margin,
            'Average profit margin across all products',
            'number',
            'success'
          )}
          {renderMetricCard(
            'High Margin Products',
            reportData.summary?.high_margin_products,
            'Products with margin > 30%',
            'number',
            'info'
          )}
          {renderMetricCard(
            'Total Products',
            reportData.summary?.total_products_analyzed,
            'Total products analyzed',
            'number',
            'primary'
          )}
          {renderMetricCard(
            'Top Product Margin',
            Math.max(...topProducts.map(p => p.profit_margin || 0)),
            'Highest profit margin among products',
            'number',
            'warning'
          )}
        </div>

        {/* Chart and Table */}
        <div className="fin-charts-row">
          <Card className="fin-chart-card fin-chart-wide">
            <CardContent className="fin-chart-content">
              <div className="fin-chart-header">
                <BarChartIcon className="fin-chart-icon" />
                <Typography className="fin-chart-title">Top Products by Profit Margin</Typography>
              </div>
              <div className="fin-chart-container">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={topProducts} className="fin-bar-chart">
                    <CartesianGrid strokeDasharray="3 3" className="fin-chart-grid" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45} 
                      textAnchor="end" 
                      height={80}
                      interval={0}
                      className="fin-chart-axis"
                    />
                    <YAxis 
                      yAxisId="left"
                      tickFormatter={(value) => `${value}%`}
                      className="fin-chart-axis"
                    />
                    <YAxis 
                      yAxisId="right"
                      orientation="right"
                      tickFormatter={(value) => `LKR ${value / 1000}k`}
                      className="fin-chart-axis"
                    />
                    <RechartsTooltip 
                      formatter={(value, name) => {
                        if (name === 'Profit Margin') return [`${value}%`, name];
                        return [formatLKR(value), name];
                      }}
                      className="fin-chart-tooltip"
                    />
                    <Legend className="fin-chart-legend" />
                    <Bar 
                      yAxisId="left"
                      dataKey="profit_margin" 
                      fill={chartColors.positive} 
                      name="Profit Margin"
                      radius={[4, 4, 0, 0]}
                    />
                    <Line 
                      yAxisId="right"
                      type="monotone" 
                      dataKey="total_profit" 
                      stroke={chartColors.netProfit} 
                      name="Total Profit"
                      strokeWidth={2}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="fin-table-card">
            <CardContent className="fin-table-content">
              <div className="fin-table-header">
                <Equalizer className="fin-table-icon" />
                <Typography className="fin-table-title">Top Performing Products</Typography>
              </div>
              <TableContainer className="fin-table-container">
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell className="fin-table-header-cell">Product</TableCell>
                      <TableCell align="right" className="fin-table-header-cell">Margin %</TableCell>
                      <TableCell align="right" className="fin-table-header-cell">Profit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topProducts.slice(0, 10).map((product, index) => (
                      <TableRow key={index} className="fin-table-row">
                        <TableCell>
                          <Tooltip title={product.name}>
                            <Typography className="fin-table-product">
                              {product.name}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${product.profit_margin?.toFixed(1)}%`}
                            size="small"
                            className={`fin-margin-chip ${
                              product.profit_margin > 30 ? 'fin-margin-high' :
                              product.profit_margin > 15 ? 'fin-margin-medium' : 'fin-margin-low'
                            }`}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography className="fin-table-profit">
                            {formatLKR(product.total_profit)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  const renderReportContent = () => {
    switch (reportType) {
      case 'net_profit':
        return renderNetProfitAnalysis();
      case 'revenue_trends':
        return renderRevenueTrends();
      case 'profit_margins':
        return renderProfitMargins();
      default:
        return renderNetProfitAnalysis();
    }
  };

  const currentReport = reportTypes.find(r => r.value === reportType);

  return (
    <Box className="fin-reports-container">
      {/* Header */}
      <Card className="fin-header-card">
        <CardContent className="fin-header-content">
          <div className="fin-header-main">
            <Button
              startIcon={<ArrowBack />}
              onClick={() => onPageChange('all-reports')}
              className="fin-back-button"
            >
              Back to Reports
            </Button>
            <div className="fin-header-text">
              <Typography className="fin-header-title">Financial Analytics</Typography>
              <Typography className="fin-header-subtitle">
                {currentReport?.description}
              </Typography>
            </div>
          </div>
          <div className="fin-header-actions">
            <Tooltip title="Refresh Data">
              <IconButton 
                onClick={loadReportData} 
                disabled={loading}
                className="fin-refresh-button"
              >
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button
              startIcon={<Download />}
              onClick={handleExport}
              disabled={!reportData || loading}
              className="fin-export-button"
            >
              Export Excel
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Report Type Selection */}
      <Card className="fin-controls-card">
        <CardContent className="fin-controls-content">
          <Typography className="fin-controls-title">Report Type</Typography>
          <div className="fin-controls-grid">
            <FormControl fullWidth className="fin-select-control">
              <InputLabel>Select Report</InputLabel>
              <Select
                value={reportType}
                label="Select Report"
                onChange={(e) => setReportType(e.target.value)}
              >
                {reportTypes.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    <div className="fin-menu-item">
                      {type.icon}
                      {type.label}
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <DateRangeFilter
              filters={filters}
              onFilterChange={setFilters}
            />
          </div>
          {lastUpdated && (
            <div className="fin-last-updated">
              <Typography className="fin-updated-text">
                Last updated: {lastUpdated.toLocaleString()}
              </Typography>
            </div>
          )}
        </CardContent>
      </Card>

      {error && (
        <Alert 
          severity="error" 
          className="fin-error-alert"
          onClose={() => setError('')}
          action={
            <Button color="inherit" size="small" onClick={loadReportData}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="fin-loading-container">
          <CircularProgress size={60} className="fin-loading-spinner" />
          <Typography className="fin-loading-text">
            Loading financial data...
          </Typography>
        </div>
      ) : reportData ? (
        renderReportContent()
      ) : (
        <Card className="fin-empty-card">
          <CardContent className="fin-empty-content">
            <Typography className="fin-empty-text">
              No financial data available for the selected report type.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default FinancialReports;